import React, { useState } from 'react';
import { X, Send, MessageCircle, CheckCircle, Loader2, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '5561995792667';
const BREVO_MEET_URL = 'https://meet.brevo.com/alexander-justi/reuniao-video-conferencia';

const ConsultationModal = ({ bundle, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!bundle) return null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.whatsapp) {
      setError('Preencha nome, e-mail e WhatsApp.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads/consultation`, {
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        formation: bundle.title,
        message: form.message
      });
      setSubmitted(true);
    } catch (err) {
      setError('Não foi possível enviar. Tente novamente ou fale pelo WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Olá! Tenho interesse na ${bundle.title} e gostaria de conversar com um consultor.`
  )}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
      data-testid="consultation-modal"
    >
      <div
        className="bg-zinc-900 rounded-2xl max-w-md w-full border border-red-500/30 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-red-700 to-red-900 p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 bg-black/30 rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
            data-testid="consultation-close-btn"
          >
            <X size={20} />
          </button>
          <h3 className="text-white font-bold text-xl pr-8">Agende uma conversa</h3>
          <p className="text-red-100 text-sm mt-1">{bundle.title}</p>
        </div>

        {submitted ? (
          <div className="p-8 text-center" data-testid="consultation-success">
            <CheckCircle className="text-green-500 mx-auto mb-4" size={56} />
            <h4 className="text-white font-bold text-lg mb-2">Recebemos seu interesse!</h4>
            <p className="text-gray-400 text-sm mb-6">
              Um de nossos consultores entrará em contato em breve com todas as informações.
              Quer falar agora mesmo?
            </p>
            <a href={BREVO_MEET_URL} target="_blank" rel="noopener noreferrer" data-testid="consultation-schedule-btn">
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white py-3 mb-3">
                <CalendarClock className="mr-2" size={18} />
                Agendar reunião online
              </Button>
            </a>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="consultation-whatsapp-btn">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-3">
                <MessageCircle className="mr-2" size={18} />
                Falar no WhatsApp
              </Button>
            </a>
            <button onClick={onClose} className="text-gray-500 text-sm mt-4 hover:text-gray-300">
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <a href={BREVO_MEET_URL} target="_blank" rel="noopener noreferrer" data-testid="consultation-schedule-primary-btn">
              <Button type="button" className="w-full bg-red-600 hover:bg-red-700 text-white py-3">
                <CalendarClock className="mr-2" size={18} />
                Agendar reunião online
              </Button>
            </a>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-zinc-700" />
              <span className="text-gray-500 text-xs">ou deixe seus dados</span>
              <div className="flex-1 h-px bg-zinc-700" />
            </div>
            <p className="text-gray-400 text-sm">
              Deixe seus dados que entramos em contato com valores e condições personalizadas.
            </p>
            <Input
              name="name"
              placeholder="Seu nome completo"
              value={form.name}
              onChange={handleChange}
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="consultation-name-input"
            />
            <Input
              name="email"
              type="email"
              placeholder="Seu melhor e-mail"
              value={form.email}
              onChange={handleChange}
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="consultation-email-input"
            />
            <Input
              name="whatsapp"
              placeholder="WhatsApp (com DDD)"
              value={form.whatsapp}
              onChange={handleChange}
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="consultation-whatsapp-input"
            />
            <textarea
              name="message"
              placeholder="Mensagem (opcional)"
              value={form.message}
              onChange={handleChange}
              rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="consultation-message-input"
            />
            {error && <p className="text-red-400 text-sm" data-testid="consultation-error">{error}</p>}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
              data-testid="consultation-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="mr-2 animate-spin" size={18} /> Enviando...</>
              ) : (
                <><Send className="mr-2" size={18} /> Quero falar com um consultor</>
              )}
            </Button>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block text-center text-green-400 text-sm hover:text-green-300">
              ou fale direto no WhatsApp
            </a>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;
